import { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkle, Clock, Target, MapPin } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

/**
 * Pro-only insights tile. Surfaces three actionable summaries derived from
 * the owner's last 7 / 30 days of profile_view events:
 *   1. Peak viewing hour bucket  ("Most views: 11am–1pm")
 *   2. Day-intent split           ("70% of viewers were looking for today")
 *   3. Top viewing area           ("Top viewing area: Lititz")
 *
 * Renders a soft "Upgrade to Pro" placeholder for non-Pro owners so the
 * value of the upgrade is visible without exposing actual data.
 */
export default function ProInsightsCard() {
  const { user } = useAuth();
  const [range, setRange] = useState("week"); // "week" | "30d"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_pro) {
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    api
      .get("/analytics/insights", { params: { range } })
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [range, user?.is_pro]);

  // Non-Pro: locked teaser card so they can see what they'd unlock.
  if (!user?.is_pro) {
    return (
      <section
        className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-5 relative overflow-hidden"
        data-testid="pro-insights-locked"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-primary">
              <Sparkle weight="fill" className="h-3.5 w-3.5" />
              Pro insights
            </div>
            <h3 className="font-display font-semibold text-lg mt-1">
              Unlock peak hours, day intent, and top viewing areas
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Pro owners see when their truck gets the most attention, where viewers are coming from,
              and whether people are looking for today or planning ahead.
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 opacity-60 pointer-events-none">
          <InsightTile icon={Clock} label="Most views" value="—" />
          <InsightTile icon={Target} label="Looking for today" value="—" />
          <InsightTile icon={MapPin} label="Top viewing area" value="—" />
        </div>
      </section>
    );
  }

  // Pro: render the actual data
  const peakLabel = data?.peak_hour_label || "Not enough data yet";
  const todayPct =
    data?.today_intent_pct != null ? `${data.today_intent_pct}% looking for today` : "Not enough data yet";
  const topCity = data?.top_cities?.[0]?.city || "Not enough data yet";

  return (
    <section className="rounded-2xl border border-primary/30 bg-card p-5" data-testid="pro-insights-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-bold text-primary">
            <Sparkle weight="fill" className="h-3.5 w-3.5" />
            Pro insights
          </div>
          <h3 className="font-display font-semibold text-lg mt-1">
            What viewers are doing
          </h3>
        </div>
        <div className="inline-flex rounded-full border border-border bg-muted/40 p-1 text-xs">
          {[
            { k: "week", label: "This week" },
            { k: "30d", label: "Last 30d" },
          ].map((opt) => (
            <button
              key={opt.k}
              onClick={() => setRange(opt.k)}
              className={`px-3 h-7 rounded-full font-medium transition ${
                range === opt.k
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`pro-insights-range-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <InsightTile
          icon={Clock}
          label="Most views"
          value={peakLabel}
          loading={loading}
          testId="pro-insight-peak"
        />
        <InsightTile
          icon={Target}
          label="Day intent"
          value={todayPct}
          loading={loading}
          testId="pro-insight-intent"
        />
        <InsightTile
          icon={MapPin}
          label="Top viewing area"
          value={topCity}
          loading={loading}
          testId="pro-insight-city"
        />
      </div>
    </section>
  );
}

function InsightTile({ icon: Icon, label, value, loading = false, testId }) {
  return (
    <div className="rounded-xl border border-border bg-background/50 p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon weight="duotone" className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 font-display font-semibold text-base sm:text-lg leading-tight">
        {loading ? "…" : value}
      </div>
    </div>
  );
}
